import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import Logo from "@/components/Logo";

export default function AdminPassword() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const rules = {
    minLength: next.length >= 8,
    hasUpper: /[A-Z]/.test(next),
    hasNumber: /\d/.test(next),
    matches: next.length > 0 && next === confirm,
  };
  const strong = rules.minLength && rules.hasUpper && rules.hasNumber;

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next) return toast.error("Please fill in all fields.");
    if (next !== confirm) return toast.error("New passwords do not match.");
    if (next.length < 8) return toast.error("New password must be at least 8 characters.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Please sign in again.");
      await logout();
      navigate("/admin/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Failed to change password."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-password-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808]">
        <div className="max-w-4xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Link
            to="/admin"
            data-testid="admin-password-back"
            className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37] tracking-wide"
          >
            <ArrowLeft size={14} /> Back to Dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-6 md:px-8 py-12">
        <div className="label-eyebrow mb-3">Account Security</div>
        <h1 className="font-display text-4xl md:text-5xl text-white mb-2" style={{ fontWeight: 300 }}>
          Change <span className="italic text-[#D4AF37]">password</span>
        </h1>
        <p className="text-white/50 text-sm mb-8">
          Signed in as <span className="text-white/80">{user?.email}</span>
        </p>

        <form
          onSubmit={submit}
          data-testid="admin-password-form"
          className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8 space-y-6"
        >
          <Field
            label="Current password"
            value={current}
            onChange={setCurrent}
            show={showCurrent}
            onToggle={() => setShowCurrent((s) => !s)}
            autoFocus
            testId="password-current"
          />

          <Field
            label="New password"
            value={next}
            onChange={setNext}
            show={showNext}
            onToggle={() => setShowNext((s) => !s)}
            testId="password-new"
          />

          <Field
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            show={showNext}
            hideToggle
            testId="password-confirm"
          />

          {/* Strength rules */}
          <ul className="text-xs space-y-1.5 border-l border-white/10 pl-4" data-testid="password-rules">
            <Rule ok={rules.minLength}>At least 8 characters</Rule>
            <Rule ok={rules.hasUpper}>Contains an uppercase letter</Rule>
            <Rule ok={rules.hasNumber}>Contains a number</Rule>
            <Rule ok={rules.matches}>New passwords match</Rule>
          </ul>

          <button
            type="submit"
            disabled={saving || !current || !next || !rules.matches}
            data-testid="admin-password-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-medium py-3 hover:bg-[#F6ECCC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {strong ? <ShieldCheck size={16} /> : <KeyRound size={16} />}
            {saving ? "Updating…" : "Update Password"}
          </button>

          <p className="text-[11px] text-white/40 leading-relaxed">
            You&apos;ll be signed out after a successful change and asked to log in with your new
            password. The change is stored securely (bcrypt-hashed) and persists across backend
            restarts.
          </p>
        </form>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, show, onToggle, hideToggle, autoFocus, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">
        {label}
      </label>
      <div className="relative">
        <input
          data-testid={testId}
          type={show ? "text" : "password"}
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#050505] border border-white/10 text-white px-4 py-3 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none pr-11"
        />
        {!hideToggle && (
          <button
            type="button"
            onClick={onToggle}
            data-testid={`${testId}-toggle`}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80"
            tabIndex={-1}
          >
            {show ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        )}
      </div>
    </div>
  );
}

function Rule({ ok, children }) {
  return (
    <li className={`flex items-center gap-2 ${ok ? "text-emerald-400" : "text-white/40"}`}>
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full ${
          ok ? "bg-emerald-400" : "bg-white/25"
        }`}
      />
      {children}
    </li>
  );
}
